import { useEffect, useMemo, useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Reveal from "@/components/base/Reveal";
import Seo from "@/components/feature/Seo";
import FaqAccordion, { buildFaqPageSchema } from "@/components/feature/FaqAccordion";
import EnquiryModal from "@/components/feature/EnquiryModal";
import { brand } from "@/mocks/siteData";
import { experienceFilterGroups } from "@/mocks/experienceCatalog";
import { tripPresets } from "@/mocks/tripPlanner";
import { usePublicExperiences } from "@/hooks/usePublicCatalog";
import { usePublicFaqs } from "@/hooks/usePublicContent";
import { experiencesFaqs } from "./experiencesFaqs";
import ExperiencesListHero from "./components/ExperiencesListHero";
import ExperiencesListHelp from "./components/ExperiencesListHelp";
import ExperienceFilterBar from "./components/ExperienceFilterBar";
import ExperienceListCard from "./components/ExperienceListCard";
import ExperiencesListCta from "./components/ExperiencesListCta";

type GroupId = "audience" | "season" | "duration";

interface Selected {
  audience: string[];
  season: string[];
  duration: string[];
}

const emptySelection: Selected = { audience: [], season: [], duration: [] };

export default function ExperiencesList() {
  const [selected, setSelected] = useState<Selected>(emptySelection);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const { data: items, loading, error, reload } = usePublicExperiences();
  const faqs = usePublicFaqs("Experiences", experiencesFaqs);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const summaries = useMemo(() => items.map((item) => item.summary), [items]);
  const details = useMemo(
    () => Object.fromEntries(items.map((item) => [item.summary.slug, item.detail])),
    [items],
  );
  const facets = useMemo(
    () => Object.fromEntries(items.map((item) => [item.summary.slug, item.facets])),
    [items],
  );

  const itineraryOptions = useMemo(
    () => [
      { value: "Custom itinerary", label: "Custom itinerary — built for me" },
      ...tripPresets.map((preset) => ({
        value: preset.title,
        label: `${preset.title} · ${preset.duration}`,
      })),
    ],
    [],
  );

  const handleToggle = (groupId: GroupId, optionId: string) => {
    setSelected((prev) => {
      const current = prev[groupId];
      const next = current.includes(optionId)
        ? current.filter((id) => id !== optionId)
        : [...current, optionId];
      return { ...prev, [groupId]: next };
    });
  };

  const handleClear = () => setSelected(emptySelection);

  const filtered = useMemo(() => {
    const facetKey: Record<GroupId, keyof (typeof facets)[string]> = {
      audience: "audiences",
      season: "seasons",
      duration: "durations",
    };

    return summaries.filter((exp) => {
      const itemFacets = facets[exp.slug];
      if (!itemFacets) return true;

      return (Object.keys(facetKey) as GroupId[]).every((groupId) => {
        const active = selected[groupId];
        if (active.length === 0) return true;
        const values = itemFacets[facetKey[groupId]];
        return active.some((id) => values.includes(id));
      });
    });
  }, [selected, summaries, facets]);

  return (
    <div className="min-h-screen bg-background-50 overflow-x-hidden">
      <Seo
        title={`Farm & Village Experiences in Konaseema | ${brand.name}`}
        description={`Browse ${summaries.length} curated farm and village experiences across Konaseema — from a day in the fields to a full rural immersion.`}
        path="/experiences"
        keywords="farm experiences Konaseema, agri tourism Andhra Pradesh, village experiences, farm visit India, rural experiences, agritourism activities"
        image="https://readdy.ai/api/search-image?query=Wide%20cinematic%20view%20of%20several%20green%20farm%20experiences%20in%20rural%20South%20India%2C%20paddy%20fields%2C%20coconut%20groves%20and%20a%20village%20path%20at%20early%20morning%2C%20lush%20green%20tones%2C%20painterly%20stylized%20agricultural%20landscape%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1920&height=1080&seq=experiences-list-hero&orientation=landscape"
        structuredData={buildFaqPageSchema(faqs)}
      />
      <Navbar />
      <main>
        <ExperiencesListHero count={summaries.length || 8} />
        <ExperiencesListHelp onEnquire={() => setEnquiryOpen(true)} />

        <section className="sun-glaze bg-background-100 py-16 md:py-24">
          <div className="w-full px-4 md:px-8">
            <Reveal className="mb-8 md:mb-10">
              <ExperienceFilterBar
                groups={experienceFilterGroups}
                selected={selected}
                onToggle={handleToggle}
                onClear={handleClear}
                resultCount={filtered.length}
                totalCount={summaries.length}
              />
            </Reveal>

            {loading && items.length === 0 ? (
              <div className="py-20 flex flex-col items-center gap-4 text-foreground-600">
                <span className="w-12 h-12 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin"></span>
                <p className="text-sm">Loading experiences…</p>
              </div>
            ) : error && items.length === 0 ? (
              <Reveal className="bg-background-50 rounded-lg border border-background-200 py-16 px-6 text-center">
                <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
                  <i className="ri-signal-wifi-error-line text-3xl"></i>
                </span>
                <h3 className="font-heading text-2xl text-foreground-950 font-semibold mt-5">
                  We couldn't load the experiences
                </h3>
                <p className="mt-3 text-foreground-600 leading-relaxed max-w-md mx-auto">{error}</p>
                <button
                  type="button"
                  onClick={reload}
                  className="mt-7 inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-7 py-3.5 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-refresh-line"></i>
                  Try again
                </button>
              </Reveal>
            ) : filtered.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {filtered.map((exp, index) => (
                  <Reveal key={exp.slug} delay={(index % 3) * 80}>
                    <ExperienceListCard
                      experience={exp}
                      detail={details[exp.slug]}
                      facets={facets[exp.slug]}
                    />
                  </Reveal>
                ))}
              </div>
            ) : (
              <Reveal className="bg-background-50 rounded-lg border border-background-200 py-16 px-6 text-center">
                <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                  <i className="ri-search-eye-line text-3xl"></i>
                </span>
                <h3 className="font-heading text-2xl text-foreground-950 font-semibold mt-5">
                  No experiences match those filters
                </h3>
                <p className="mt-3 text-foreground-600 leading-relaxed max-w-md mx-auto">
                  Try removing a filter or two — how about clearing them all and exploring every
                  experience we offer?
                </p>
                <button
                  type="button"
                  onClick={handleClear}
                  className="mt-7 inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-7 py-3.5 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-refresh-line"></i>
                  Clear all filters
                </button>
              </Reveal>
            )}
          </div>
        </section>

        <FaqAccordion
          faqs={faqs}
          eyebrow="Before You Book"
          title="Is Agri-Tourism Right for Me?"
          intro="Honest answers for anyone wondering whether a day on a real, working farm is for them."
        />

        <ExperiencesListCta onEnquire={() => setEnquiryOpen(true)} />
      </main>
      <Footer />
      <EnquiryModal
        open={enquiryOpen}
        onClose={() => setEnquiryOpen(false)}
        kind="trip"
        options={itineraryOptions}
        defaultValue="Custom itinerary"
      />
    </div>
  );
}